import { Product, ProductTenor, ProductType } from '@protos/product';
import { OrderBookTopMessage } from '@protos/v2/orderBookTop';
import { Ticker } from '@protos/v2/ticker';
import { streamV2OrderBookTop, streamV2Product } from '@services/context';
import { NotificationPlayer } from '@shared/assets/sounds/NotificationPlayer';
import { AgEventListener, GridApi, GridReadyEvent, IRowNode } from 'ag-grid-enterprise';
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../../../common/store/AppStore';
import { getRFQSymbolForMonthly, getRFQSymbolForSingleTenor, getRFQSymbolForSpread } from '../../trading/utils';
import { showWatchlistToast } from '../WatchlistNotificationToast';
import {
  ESide,
  getWatchlistEntryFromRecord,
  getWatchlistRecordFromEntry,
  WatchlistEntry,
  WatchlistEntryOptions,
  WatchlistRecord,
} from '../WatchlistTypes';

const defaultOptions: WatchlistEntryOptions = {
  notifications: true,
  sounds: 0,
  showBid: false,
  showAsk: false,
};

export const useWatchlistStream = (
  editWidgetCallback: (id: string, payload: Object) => void,
  widgetId: string,
  addTradeWidgetCallback: (entry: WatchlistEntry) => void,
  initialRecords: WatchlistRecord[] = []
) => {
  const [products, setProducts] = useState<string[]>(initialRecords.map(r => r.id));
  const [symbols, setSymbols] = useState<string[]>(initialRecords.map(r => r.symbol));
  const editWidgetCallbackRef = useRef(editWidgetCallback);

  useEffect(() => {
    editWidgetCallbackRef.current = editWidgetCallback;
  }, [editWidgetCallback]);

  // To stop react constantly re-rendering the entire widget whenever there's a single value
  // change in the data, we're maintaining two lists of entries- live data in a ref for use
  // in the grid, and a stateful version for the regular React render cycle.
  //
  // The latter is derived from the former, and updated less frequently.

  // Live data
  const liveWatchlistData = useRef<WatchlistEntry[]>(initialRecords.map(getWatchlistEntryFromRecord));

  // Stateful data
  const [watchlistEntries, setWatchlistEntries] = useState(liveWatchlistData.current);

  // Reference to the grid API
  const gridAPIRef = useRef<GridApi | null>(null);

  const { user } = useAppStore();

  const canUserTradeProduct = (product: Product | undefined) => {
    return !!user?.trade_limits.find(tl => tl.symbol === product?.symbol);
  };

  // Add a new entry to the watchlist
  const addNewWatch = async (
    product: Product,
    tenor: ProductTenor | null,
    frontTenor: ProductTenor | null,
    backTenor: ProductTenor | null,
    targetPrice: number,
    options?: WatchlistEntryOptions
  ): Promise<void> => {
    let subscriptionKey = '';

    if (tenor) {
      if (product.product_type === ProductType.CALENDAR) {
        subscriptionKey = getRFQSymbolForMonthly(product, tenor);
      } else {
        subscriptionKey = getRFQSymbolForSingleTenor(product, tenor);
      }
    } else if (frontTenor && backTenor) {
      // TODO: implement proper watches on big spreads
      console.warn('Non-monthly spreads not currently supported');
      subscriptionKey = await getRFQSymbolForSpread(product, frontTenor, backTenor);
    }

    if (watchlistEntries.find(e => e.id === subscriptionKey)) return;

    const newEntry: WatchlistEntry = {
      id: subscriptionKey,
      symbol: product.symbol,
      targetPrice,
      side: ESide.UNSET,
      // TODO: this is used for display, but spreads can be a bit verbose, so may want something better
      tenor: tenor?.display || subscriptionKey,
      tenorCode: tenor?.code || '',
      targetPriceHit: false,
      targetPriceHitTime: -1,
      subscriptionKey,
      options: { ...defaultOptions, ...options },
      hasData: false,
      referencePrice: 0,
      currentPrice: 0,
      lastPrice: 0,
      spread: { bid: 0, mid: 0, ask: 0 },
      hasUpdated: false,
      hasSpreadData: false,
      canTrade: canUserTradeProduct(product),
    };

    liveWatchlistData.current = [...liveWatchlistData.current, newEntry];

    setWatchlistEntries(liveWatchlistData.current);

    setProducts([...products, subscriptionKey]);

    if (!symbols.includes(newEntry.symbol)) {
      setSymbols([...symbols, product.symbol]);
    }

    editWidgetCallbackRef.current(widgetId, { records: [...initialRecords, getWatchlistRecordFromEntry(newEntry)] });
  };

  const removeWatch = (id: string): boolean => {
    if (liveWatchlistData.current.length === 0) {
      console.error('Something has gone horribly wrong');
    }

    const newEntries = liveWatchlistData.current.filter(e => {
      return e.id !== id;
    });

    liveWatchlistData.current = newEntries;

    setWatchlistEntries(newEntries);

    setProducts(newEntries.map(r => r.id));
    setSymbols(Array.from(new Set(newEntries.map(r => r.symbol))));

    editWidgetCallbackRef.current(widgetId, { records: newEntries.map(e => getWatchlistRecordFromEntry(e)) });

    return true;
  };

  const updateLiveDataPrice = (entry: WatchlistEntry, index: number, forceGridUpdate: boolean = false) => {
    if (!gridAPIRef.current) return;

    const newEntries = [...liveWatchlistData.current];
    newEntries[index] = entry;
    liveWatchlistData.current = newEntries;

    const row: IRowNode<WatchlistEntry> | undefined = gridAPIRef.current.getRowNode(entry.id);

    if (row) {
      row.updateData(entry);
    } else {
      console.error(`No row with id ${entry.id} in grid...`);
    }

    if (forceGridUpdate) {
      gridAPIRef.current.refreshCells({ force: true });
      gridAPIRef.current.onSortChanged();
    }
  };

  // To ensure old callbacks aren't kept around we generate a new one each time the symbols
  // change, and stop subscriptions to the old one
  const getTickerCallback = () => {
    return (ticker: Ticker) => {
      const entry = liveWatchlistData.current.find(r => r.id === ticker.symbol);

      if (!entry) return;
      if (!gridAPIRef.current) return;

      const entryIndex = liveWatchlistData.current.indexOf(entry);

      let forceGridUpdate: boolean = false;
      entry.lastPrice = entry.currentPrice;
      entry.currentPrice = Number(ticker.mid);
      entry.spread.mid = Number(ticker.mid);
      entry.hasUpdated = true;

      // If this is the first update, it might be the first time we get a price
      // So, we need to work out if this should be shown as a buy or a sell

      if (!entry.hasData) {
        entry.side = entry.currentPrice > entry.targetPrice ? ESide.BUY : ESide.SELL;
        entry.hasData = true;
        forceGridUpdate = true;
      }

      // Check if the price has been hit
      else if (
        !entry.targetPriceHit &&
        ((entry.side === ESide.BUY && entry.currentPrice < entry.targetPrice) ||
          (entry.side === ESide.SELL && entry.currentPrice > entry.targetPrice))
      ) {
        entry.targetPriceHit = true;
        entry.targetPriceHitTime = new Date().getTime();

        if (entry.options.sounds > 0) {
          NotificationPlayer.get().playSoundByIndex(entry.options.sounds);
        }
        if (entry.options.notifications) {
          showWatchlistToast(entry, addTradeWidgetCallback);
        }
        forceGridUpdate = true;
      }

      updateLiveDataPrice(entry, entryIndex, forceGridUpdate);
    };
  };

  const tickerCallbackRef = useRef<(ticker: Ticker) => void>(getTickerCallback());

  // This contains the bid and ask (except it's labelled buy and sell and THIS IS VERY CONFUSING AT TIMES)
  const getOrderbookTopCallback = () => {
    return (orderBookTop: OrderBookTopMessage) => {
      const { symbol, buy, sell } = orderBookTop;
      const indexToUpdate = liveWatchlistData.current.findIndex(e => e.subscriptionKey === symbol);

      if (indexToUpdate === -1) {
        return;
      }

      const newEntry = { ...liveWatchlistData.current[indexToUpdate] };
      const ask = Number(sell.price);
      const bid = Number(buy.price);

      if (!isNaN(ask)) newEntry.spread.ask = ask;
      if (!isNaN(bid)) newEntry.spread.bid = bid;
      newEntry.hasSpreadData = true;

      updateLiveDataPrice(newEntry, indexToUpdate);
    };
  };

  const orderBookTopCallbackRef = useRef<(orderBookTop: OrderBookTopMessage) => void>(getOrderbookTopCallback());

  // Updates an entry in the watchlist, as well as the live data.
  const updateWatch = (id: string, update: Partial<Omit<WatchlistEntry, 'id' | 'subscriptionKey' | 'hasData'>>) => {
    if (!gridAPIRef.current) return;

    const index = liveWatchlistData.current.findIndex(e => e.id === id);
    if (index === -1) return;

    const currentRecord = liveWatchlistData.current[index];

    const updateKeys = Object.keys(update);

    const entryToUpdate: WatchlistEntry = {
      ...currentRecord,
      ...update,
    };

    // If they've entered a new target price, we may need to switch this from 'buy' to 'sell'
    // Going to assume for now that they're not entering a price that is asymptotically close
    // to the current price
    if (updateKeys.includes('targetPrice') && currentRecord.hasData) {
      entryToUpdate.side = update.targetPrice! > currentRecord.currentPrice ? ESide.SELL : ESide.BUY;
    }

    const newEntries = [...liveWatchlistData.current];
    newEntries[index] = entryToUpdate;

    liveWatchlistData.current = newEntries;
    setWatchlistEntries(liveWatchlistData.current);

    editWidgetCallbackRef.current(widgetId, { records: newEntries.map(e => getWatchlistRecordFromEntry(e)) });
  };

  // Used when editing a cell (currently just the target price for now)
  const onCellEditCompleteCallback: AgEventListener<any, any, 'cellEditingStopped'> = event => {
    const { data, newValue, colDef } = event;

    if (!data || colDef.field !== 'targetPrice' || typeof newValue !== 'number') {
      return;
    }

    const { id } = data;
    const toUpdate = liveWatchlistData.current.find(r => r.id === id);
    const updateIndex = liveWatchlistData.current.findIndex(r => r.id === id);

    if (!toUpdate || updateIndex === -1) {
      console.log(`No entry found with id ${id}`);
      return;
    }

    if (!toUpdate.hasData) {
      toUpdate.side = ESide.UNSET;
    } else {
      toUpdate.side = newValue > toUpdate.currentPrice ? ESide.SELL : ESide.BUY;
    }

    toUpdate.targetPrice = newValue;
    toUpdate.targetPriceHit = false;
    toUpdate.targetPriceHitTime = -1;

    const newEntries = [...liveWatchlistData.current];
    newEntries[updateIndex] = toUpdate;

    liveWatchlistData.current = newEntries;
    setWatchlistEntries(newEntries);

    editWidgetCallbackRef.current(widgetId, { records: newEntries.map(e => getWatchlistRecordFromEntry(e)) });
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridAPIRef.current = params.api as GridApi<WatchlistEntry>;
    gridAPIRef.current.addEventListener('cellEditingStopped', onCellEditCompleteCallback);
  };

  const prevSymbols = useRef<string[]>([]);

  // Stream handling - update subscriptions when symbols change
  useEffect(() => {
    const newSubscriptions = symbols.filter(s => !prevSymbols.current.includes(s));

    streamV2Product.unsubscribe(prevSymbols.current, tickerCallbackRef.current);
    tickerCallbackRef.current = getTickerCallback();
    streamV2Product.subscribe(symbols, tickerCallbackRef.current);

    streamV2OrderBookTop.unsubscribe(prevSymbols.current, orderBookTopCallbackRef.current);
    orderBookTopCallbackRef.current = getOrderbookTopCallback();
    streamV2OrderBookTop.subscribe(symbols, orderBookTopCallbackRef.current);

    prevSymbols.current = newSubscriptions;

    return () => {
      streamV2Product.unsubscribe(symbols, tickerCallbackRef.current);
      streamV2OrderBookTop.unsubscribe(symbols, orderBookTopCallbackRef.current);
    };
  }, [symbols]);

  return {
    entries: watchlistEntries,
    addNewWatch,
    updateWatch,
    removeWatch,
    onGridReady,
    symbols: products,
  };
};
